import { useCallback, useEffect, useState } from "react";
import { ConnectionCard } from "@/pages/admin/ConnectionCard";
import { toast } from "sonner";
import { fmtDate } from "@/constants/sports";
import { api, formatApiError } from "@/lib/api";

const STATUS = {
  success: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10",
  failed: "text-red-400 border-red-500/30 bg-red-500/10",
  skipped: "text-amber-400 border-amber-500/30 bg-amber-500/10",
  running: "text-blue-400 border-blue-500/30 bg-blue-500/10",
  partial: "text-orange-400 border-orange-500/30 bg-orange-500/10",
};
const time = (iso) => (iso ? `${fmtDate(iso)} ${new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : "—");

export const SyncTab = () => {
  const [data, setData] = useState({ items: [] });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/syncs").then((r) => setData(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const running = data.items.some((s) => s.status === "running");
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [running, load]);

  const syncNow = async () => {
    setBusy(true);
    try {
      await api.post("/admin/sync");
      toast.success("FanFlare sync started");
      setTimeout(() => { load(); setBusy(false); }, 1500);
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <ConnectionCard onSync={syncNow} busy={busy || running} />
      <div className="overflow-x-auto rounded-xl border border-slate-800">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-[#131927]">
            <tr className="text-left">{["Started", "Trigger", "Status", "Added", "Updated", "Protected", "Skipped", "Games", "Details"].map((h) => <th key={h} className="eyebrow px-5 py-3 font-normal">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {data.items.map((s) => (
              <tr key={s.id} data-testid={`sync-row-${s.id}`}>
                <td className="px-5 py-4 font-data text-xs text-slate-300">{time(s.started_at)}</td>
                <td className="px-5 py-4 capitalize text-slate-300">{s.trigger}</td>
                <td className="px-5 py-4"><span className={`rounded-full border px-2.5 py-0.5 font-data text-[11px] uppercase ${STATUS[s.status]}`}>{s.status}</span></td>
                <td className="px-5 py-4 font-data">{s.added}</td>
                <td className="px-5 py-4 font-data">{s.updated}</td>
                <td className="px-5 py-4 font-data">{s.skipped_protected}</td>
                <td className="px-5 py-4 font-data">{s.skipped ?? 0}</td>
                <td className="px-5 py-4 font-data">{s.games ?? 0}</td>
                <td className="max-w-sm px-5 py-4 text-xs text-slate-400">
                  <div>{s.message}</div>
                  {Object.entries(s.per_sport || {}).map(([sp, r]) => (
                    <div key={sp} data-testid={`sync-row-${s.id}-${sp}`} className="mt-1 font-data text-[11px] text-slate-500">
                      {sp}: +{r.added} / {r.updated} upd / {r.skipped_protected} prot / {r.skipped} skip / {r.games} games{r.note ? ` · ${r.note}` : ""}
                    </div>
                  ))}
                  {s.errors?.length > 0 && <div className="mt-1 text-red-400">{s.errors.slice(0, 3).join("; ")}{s.errors.length > 3 ? ` (+${s.errors.length - 3} more)` : ""}</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!data.items.length && <p data-testid="sync-empty" className="p-10 text-center text-sm text-slate-500">No syncs have run yet.</p>}
      </div>
    </div>
  );
};
